import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { useDataProvider, useLocalization } from '@ui-framework/index';
import IconLibrary from '../../../../components/IconLibrary';
import { MY_NOTIFICATIONS_API_PATH } from '../../apiPaths';
import NotificationListItems from '../NotificationListItems';
import useNotificationInbox from '../../store/useNotificationInbox';

const DROPDOWN_LIMIT = 5;
const BUTTON_ID = 'notification-bell-button';
const MENU_ID = 'notification-bell-menu';

function NotificationBell() {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const provider = useDataProvider(
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default',
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const requestIdRef = useRef(0);
  const requestControllerRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    requestControllerRef.current = controller;
    return () => {
      requestIdRef.current += 1;
      controller.abort();
    };
  }, []);

  const loadRecent = useCallback(
    async (signal = requestControllerRef.current?.signal) => {
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      setLoading(true);
      setLoadError(false);
      try {
        const result = await provider.get(
          MY_NOTIFICATIONS_API_PATH,
          { page: 1, limit: DROPDOWN_LIMIT, unreadOnly: true },
          {
            suppressGlobalErrorRedirect: true,
            allowTokenRenewal: true,
            signal,
          },
        );
        if (requestId !== requestIdRef.current) {
          return;
        }
        if (!result?.ok) {
          setLoadError(true);
          throw new Error('Notification list refresh failed');
        }
        setNotifications(result?.data?.data ?? []);
      } catch (error) {
        if (requestId === requestIdRef.current) {
          setLoadError(true);
        }
        throw error;
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [provider],
  );

  const { markRead, refresh, unreadCount } = useNotificationInbox({
    setNotifications,
    removeOnRead: true,
    reloadOnReadFailure: () => loadRecent().catch(() => undefined),
    refreshNotifications: loadRecent,
  });

  const openDropdown = async event => {
    setAnchorEl(event.currentTarget);
    await refresh();
  };
  const closeDropdown = () => setAnchorEl(null);
  const openNotification = async notification => {
    await markRead(notification);
    closeDropdown();
    if (notification?.linkPath) {
      navigate(notification.linkPath);
    }
  };

  const bellLabel =
    unreadCount > 0
      ? formatMessage(
          {
            id: 'NOTIFICATIONS_BELL_LABEL_WITH_COUNT',
            defaultMessage:
              '{count, plural, one {Notifications, # unread} other {Notifications, # unread}}',
          },
          { count: unreadCount },
        )
      : formatMessage({
          id: 'NOTIFICATIONS',
          defaultMessage: 'Notifications',
        });

  return (
    <>
      <Tooltip title={bellLabel}>
        <IconButton
          onClick={openDropdown}
          id={BUTTON_ID}
          aria-label={bellLabel}
          aria-haspopup="dialog"
          aria-expanded={Boolean(anchorEl)}
          aria-controls={MENU_ID}
          size="large"
          color="primary"
          sx={{ mx: 1 }}>
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <IconLibrary.Alerts />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        id={MENU_ID}
        sx={{ mt: '45px' }}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(anchorEl)}
        onClose={closeDropdown}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-labelledby': BUTTON_ID,
            sx: { width: 380, maxWidth: '100%' },
          },
        }}>
        <List disablePadding>
          <Box
            component="li"
            sx={{
              px: 2,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <Typography variant="subtitle1">
              {formatMessage({
                id: 'NOTIFICATIONS',
                defaultMessage: 'Notifications',
              })}
            </Typography>
            <Button
              size="small"
              startIcon={<IconLibrary.NotificationPreferences />}
              onClick={() => {
                closeDropdown();
                navigate('/notifications/preferences');
              }}>
              {formatMessage({
                id: 'PREFERENCES',
                defaultMessage: 'Preferences',
              })}
            </Button>
          </Box>
          <Divider component="li" />
          {loadError ? (
            <Box component="li" sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {formatMessage({
                  id: 'COULDNT_LOAD_NOTIFICATIONS_TRY_AGAIN_LATER',
                  defaultMessage:
                    "Couldn't load notifications. Try again later.",
                })}
              </Typography>
            </Box>
          ) : (
            <NotificationListItems
              notifications={notifications}
              loading={loading}
              onSelect={openNotification}
              emptyMessage={formatMessage({
                id: 'YOURE_ALL_CAUGHT_UP',
                defaultMessage: "You're all caught up",
              })}
            />
          )}
          <Divider component="li" />
          <Box component="li" sx={{ px: 2, py: 1, textAlign: 'center' }}>
            <Button
              size="small"
              onClick={() => {
                closeDropdown();
                navigate('/notifications');
              }}>
              {formatMessage({
                id: 'VIEW_ALL_NOTIFICATIONS',
                defaultMessage: 'View all notifications',
              })}
            </Button>
          </Box>
        </List>
      </Popover>
    </>
  );
}

export default observer(NotificationBell);
